import { Network, Zap, ShieldCheck, Server, ScanEye, Save, Plug, Mic } from 'lucide-react';
import SeriesTemplate from '../components/SeriesTemplate';

/**
 * IPカメラシリーズ — the general-purpose HS IP camera lineup
 * (dome + bullet, PoE, 5MP/8MP), suitable for most standard installations.
 */
const IpCamera = () => {
  const features = [
    {
      Icon: Network,
      title: 'PoE対応で配線がシンプル',
      desc: 'LANケーブル1本で電源と映像を伝送でき、施工・保守がしやすい構成です。',
    },
    {
      Icon: ScanEye,
      title: '5MP / 8MPの高解像度',
      desc: '広範囲を高精細に記録でき、後からの確認や拡大にも対応します。',
    },
    {
      Icon: ShieldCheck,
      title: 'IP66屋外対応モデル',
      desc: '雨風の当たる屋外や車庫・駐車場・施設外周などにも設置できます。',
    },
    {
      Icon: Server,
      title: 'NVR・録画機器と連携',
      desc: 'カメラ台数・録画日数・保存容量に合わせて録画機器を選定できます。',
    },
    {
      Icon: Save,
      title: 'microSD対応',
      desc: '本体側でのバックアップ録画も可能で、通信断時の保険としても活用できます。',
    },
    {
      Icon: Mic,
      title: '音声対応モデルあり',
      desc: '内蔵マイクや音声入力に対応したモデルもあり、現場の音声確認に使えます。',
    },
  ];

  const models = [
    {
      name: 'HS-IE40BBWシリーズ',
      type: 'ドーム型',
      image: '/images/hs-ie40bbw.png',
      diagram: '/images/hs-ie40bbw-diagram.png',
      modelList: ['HS-BC-IE40BBW-5M3', 'HS-BC-IE40BBW-8M6'],
      specs: [
        '5MP / 8MP',
        '電動バリフォーカル 5X 2.7〜13.5mm',
        '赤外線 最大35m / 白色 最大20m',
      ],
    },
    {
      name: 'HS-IE35BBWシリーズ',
      type: 'ドーム型',
      image: '/images/hs-ie35bbw.png',
      diagram: '/images/hs-ie35bbw-diagram.png',
      modelList: ['HS-BC-IE35BBW-5M3', 'HS-BC-IE35BBW-8M6'],
      specs: [
        '5MP / 8MP',
        '電動バリフォーカル 5X 2.7〜13.5mm',
        '赤外線 最大35m / 白色 最大20m',
      ],
    },
    {
      name: 'HS-IB682BWシリーズ',
      type: 'バレット型',
      image: '/images/hs-ib682bw.png',
      diagram: '/images/hs-ib682bw-diagram.png',
      modelList: ['HS-BC-IB682BW-5M3', 'HS-BC-IB682BW-8M6', 'HS-BC-IB682BW-8MF'],
      specs: ['5MP / 8MP', '固定レンズ 2.8mm', '赤外線 最大25m / 白色 最大15m'],
    },
    {
      name: 'HS-IB788BWシリーズ',
      type: 'バレット型',
      image: '/images/hs-ib788bw.png',
      diagram: '/images/hs-ib788bw-diagram.png',
      modelList: ['HS-BC-IB788BW-5M3', 'HS-BC-IB788BW-8M6', 'HS-BC-IB788BW-8MF'],
      specs: [
        '5MP / 8MP',
        '電動バリフォーカル 5X 2.7〜13.5mm',
        '赤外線 最大40m / 白色 最大25m',
      ],
    },
  ];

  const commonBadges = [
    { Icon: Zap, label: 'PoE対応' },
    { Icon: Plug, label: 'DC12V対応' },
    { Icon: ShieldCheck, label: 'IP66' },
    { Icon: Save, label: 'microSD対応' },
    { Icon: ScanEye, label: '人・車両検知' },
    { Icon: Mic, label: '音声対応' },
  ];

  const compareRows = [
    ['HS-IE40BBWシリーズ', 'ドーム型', '電動バリフォーカル', '5MP / 8MP', '赤外線 最大35m / 白色 最大20m', '出入口・施設外周・屋外監視'],
    ['HS-IE35BBWシリーズ', 'ドーム型', '電動バリフォーカル', '5MP / 8MP', '赤外線 最大35m / 白色 最大20m', '出入口・施設まわり'],
    ['HS-IB682BWシリーズ', 'バレット型', '固定レンズ 2.8mm', '5MP / 8MP', '赤外線 最大25m / 白色 最大15m', '駐車場・資材置場・屋外設備'],
    ['HS-IB788BWシリーズ', 'バレット型', '電動バリフォーカル', '5MP / 8MP', '赤外線 最大40m / 白色 最大25m', '広めの駐車場・施設外周・車両出入口まわり'],
  ];

  const notes = [
    '解像度・レンズ・照射距離は設置環境や対象範囲により選定が必要です。',
    '屋外設置の場合は、配線経路・取付位置・防水処理の確認が必要です。',
    '録画日数や保存容量は、カメラ台数・解像度・NVR容量により異なります。',
    '機種や設定内容により、使用できる機能が異なる場合があります。',
  ];

  return (
    <SeriesTemplate
      id="ip"
      eyebrow="IP CAMERA"
      name="IPカメラシリーズ"
      description="PoE対応・5MP/8MPの標準的な防犯カメラシリーズです。店舗・オフィス・工場・施設外周など、幅広い現場に対応します。"
      tags={['PoE対応', '5MP / 8MP', 'IP66', 'microSD対応', '人・車両検知', '音声対応']}
      heroImage="/images/hs-ib788bw.png"
      features={features}
      locations={['店舗・商業施設', 'オフィス・事務所', '工場・倉庫', '施設外周', '駐車場', 'マンション・集合住宅', '学校・教育施設', '公共施設']}
      models={models}
      commonBadges={commonBadges}
      compareRows={compareRows}
      notes={notes}
    />
  );
};

export default IpCamera;
